import { useEffect, useState } from "react";
import { Card, Col, Row, Statistic, Spin, Typography, Badge, Progress, Empty, Select, Segmented, Button, Space, AutoComplete, Input } from "antd";
import {
  TeamOutlined,
  UserOutlined,
  CalendarOutlined,
  DollarOutlined,
  RiseOutlined,
  EnvironmentOutlined,
  FilterOutlined,
  ReloadOutlined,
  BarChartOutlined,
  PlayCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useGetDashboardStats } from "../services/dashboard.service";
import { useNavigate } from "react-router-dom";
import AiAgentWidget from "./ai-agent-widget";

const { Title, Text } = Typography;

const COLORS = ["#6366f1", "#0891b2", "#f59e0b", "#10b981", "#f43f5e", "#a78bfa"];
const PAGE_BG = "#f5f6fa";
const CARD_BG = "#ffffff";
const CARD_BORDER = "#e8eaf0";
const TEXT_PRIMARY = "#1e293b";
const TEXT_MUTED = "#64748b";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

const kpiCards = (stats: any) => [
  {
    key: "travellers",
    title: "Total Travellers",
    value: stats?.totalTravellers ?? 0,
    subValue: `${stats?.activeTravellers ?? 0} active`,
    icon: <TeamOutlined />,
    color: "#6366f1",
    gradient: "linear-gradient(135deg, #6366f1 0%, #818cf8 100%)",
    route: "/travellers",
  },
  {
    key: "attendants",
    title: "Total Attendants",
    value: stats?.totalAttendants ?? 0,
    subValue: `${stats?.activeAttendants ?? 0} active`,
    icon: <UserOutlined />,
    color: "#22d3ee",
    gradient: "linear-gradient(135deg, #0891b2 0%, #22d3ee 100%)",
    route: "/attendants",
  },
  {
    key: "trips",
    title: "Total Trips",
    value: stats?.totalTrips ?? 0,
    subValue: `${stats?.activeTrips ?? 0} active`,
    icon: <CalendarOutlined />,
    color: "#10b981",
    gradient: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
    route: "/trips?tab=all",
  },
  {
    key: "runningTrips",
    title: "Running Trips",
    value: stats?.runningTrips ?? 0,
    subValue: "currently in progress",
    icon: <PlayCircleOutlined />,
    color: "#f97316",
    gradient: "linear-gradient(135deg, #ea580c 0%, #fb923c 100%)",
    route: "/trips?tab=running",
  },
  {
    // Everything paid through the platform (captured, not refunded).
    key: "overallRevenue",
    title: "Overall Revenue",
    value: formatCurrency(stats?.totalProcessed ?? 0),
    subValue: "all payments through the platform",
    icon: <DollarOutlined />,
    color: "#0ea5e9",
    gradient: "linear-gradient(135deg, #0284c7 0%, #38bdf8 100%)",
    isCurrency: true,
    route: "/finance",
  },
  {
    key: "revenue",
    // Platform earnings only (platform fees + commission + cancellation fees). Tips,
    // surcharges and the attendant's share of trip charges are not platform earnings.
    title: "Platform Earnings",
    value: formatCurrency(stats?.totalRevenue ?? 0),
    subValue: `${formatCurrency(stats?.totalRefunds ?? 0)} refunded`,
    icon: <DollarOutlined />,
    color: "#f59e0b",
    gradient: "linear-gradient(135deg, #d97706 0%, #f59e0b 100%)",
    isCurrency: true,
    route: "/finance",
  },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [quickSearch, setQuickSearch] = useState<string>("");
  const [focus, setFocus] = useState<string>("all");
  const [monthWindow, setMonthWindow] = useState<number>(6);
  const [selectedTripStatuses, setSelectedTripStatuses] = useState<string[]>([]);
  const [selectedAttendantStatuses, setSelectedAttendantStatuses] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const getDashboardStats = useGetDashboardStats({
    onSuccess: (res: any) => {
      if (res?.statusCode === 200) {
        setData(res.data);
      }
    },
  });

  useEffect(() => {
    getDashboardStats.mutate({});
  }, []);

  const loading = getDashboardStats.isLoading;
  const stats = data?.stats;
  const tripStatusBreakdown: any[] = data?.tripsByStatus ?? [];
  const revenueByMonth: any[] = data?.revenueByMonth ?? [];
  const registrationsByMonth: any[] = data?.registrationsByMonth ?? [];
  const topCities: any[] = data?.topCities ?? [];
  const attendantStatus: any[] = data?.attendantsByStatus ?? [];
  const tripStatusOptions = tripStatusBreakdown.map((item) => ({ label: item.statusName, value: item.statusName }));
  const attendantStatusOptions = attendantStatus.map((item) => ({ label: item.statusName, value: item.statusName }));
  const cityOptions = topCities.map((item) => ({ label: item.cityName, value: item.cityName }));
  const filteredRevenueByMonth = revenueByMonth.slice(Math.max(revenueByMonth.length - monthWindow, 0));
  const filteredRegistrationsByMonth = registrationsByMonth.slice(Math.max(registrationsByMonth.length - monthWindow, 0));
  const filteredTripStatusBreakdown = selectedTripStatuses.length > 0
    ? tripStatusBreakdown.filter((item) => selectedTripStatuses.includes(item.statusName))
    : tripStatusBreakdown;
  const filteredAttendantStatus = selectedAttendantStatuses.length > 0
    ? attendantStatus.filter((item) => selectedAttendantStatuses.includes(item.statusName))
    : attendantStatus;
  const filteredTopCities = selectedCities.length > 0
    ? topCities.filter((item) => selectedCities.includes(item.cityName))
    : topCities;
  const filteredRevenueTotal = filteredRevenueByMonth.reduce((sum, item) => sum + (item.revenue ?? 0), 0);
  const filteredGrossRevenueTotal = filteredRevenueByMonth.reduce((sum, item) => sum + (item.grossRevenue ?? 0), 0);
  const filteredRegistrationTotal = filteredRegistrationsByMonth.reduce((sum, item) => sum + (item.attendants ?? 0) + (item.travellers ?? 0), 0);
  const quickSearchOptions: any[] = [
    { value: "Running Trips", route: "/trips?tab=running" },
    { value: "Upcoming Trips", route: "/trips?tab=upcoming" },
    { value: "Pending Trips", route: "/trips?tab=pending" },
    { value: "Completed Trips", route: "/trips?tab=completed" },
    { value: "All Trips", route: "/trips?tab=all" },
    { value: "Attendants", route: "/attendants" },
    { value: "Travellers", route: "/travellers" },
    { value: "Finance", route: "/finance" },
    { value: "Admin Users", route: "/adminusers" },
    { value: "Activity Logs", route: "/activitylogs" },
    ...topCities.map((city: any) => ({ value: `Trips in ${city.cityName}`, route: `/trips?tab=all&q=${encodeURIComponent(city.cityName)}` })),
  ];

  const onQuickSearch = (rawValue?: string) => {
    const value = (rawValue ?? quickSearch).trim();
    if (!value) return;

    const exact = quickSearchOptions.find((opt) => opt.value.toLowerCase() === value.toLowerCase());
    if (exact) {
      navigate(exact.route);
      return;
    }

    const lower = value.toLowerCase();
    if (lower.includes("running") && lower.includes("trip")) {
      navigate(`/trips?tab=running&q=${encodeURIComponent(value)}`);
      return;
    }
    if (lower.includes("upcoming") && lower.includes("trip")) {
      navigate(`/trips?tab=upcoming&q=${encodeURIComponent(value)}`);
      return;
    }
    if (lower.includes("pending") && lower.includes("trip")) {
      navigate(`/trips?tab=pending&q=${encodeURIComponent(value)}`);
      return;
    }
    if (lower.includes("completed") && lower.includes("trip")) {
      navigate(`/trips?tab=completed&q=${encodeURIComponent(value)}`);
      return;
    }
    if (lower.includes("attendant") || lower.includes("agent") || lower.includes("people")) {
      navigate(`/attendants?q=${encodeURIComponent(value)}`);
      return;
    }
    if (lower.includes("traveller") || lower.includes("traveler")) {
      navigate(`/travellers?q=${encodeURIComponent(value)}`);
      return;
    }
    if (lower.includes("revenue") || lower.includes("finance")) {
      navigate("/finance");
      return;
    }

    navigate(`/trips?tab=all&q=${encodeURIComponent(value)}`);
  };

  const visibleKpis = kpiCards(stats).filter((kpi) => {
    if (focus === "people") return ["Total Travellers", "Total Attendants"].includes(kpi.title);
    if (focus === "trips") return ["Total Trips", "Running Trips"].includes(kpi.title);
    if (focus === "revenue") return ["Overall Revenue", "Platform Earnings"].includes(kpi.title);
    return true;
  });
  const showRevenueSection = focus === "all" || focus === "revenue";
  const showTripsSection = focus === "all" || focus === "trips";
  const showPeopleSection = focus === "all" || focus === "people";
  const resetFilters = () => {
    setFocus("all");
    setMonthWindow(6);
    setSelectedTripStatuses([]);
    setSelectedAttendantStatuses([]);
    setSelectedCities([]);
  };

  return (
    <div
      style={{
        background: PAGE_BG,
        minHeight: "100%",
        padding: "0 4px",
        color: TEXT_PRIMARY,
      }}
    >
      <Card
        style={{
          marginBottom: 28,
          borderRadius: 24,
          border: `1px solid ${CARD_BORDER}`,
          background: CARD_BG,
          boxShadow: "0 16px 40px rgba(15, 23, 42, 0.08)",
          overflow: "hidden",
        }}
        bodyStyle={{ padding: 24 }}
      >
        <div
          style={{
            background: "rgba(255,255,255,0.82)",
            backdropFilter: "blur(10px)",
            border: `1px solid ${CARD_BORDER}`,
            borderRadius: 18,
            padding: 18,
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: TEXT_PRIMARY, fontWeight: 700 }}>
              <FilterOutlined />
              Filter Dashboard
            </div>
            <Button icon={<ReloadOutlined />} onClick={() => getDashboardStats.mutate({})}>
              Refresh
            </Button>
          </div>
          <Row gutter={[12, 12]}>
                <Col xs={24} md={12}>
                  <Text style={{ color: TEXT_MUTED, fontSize: 14, display: "block", marginBottom: 6 }}>Focus</Text>
                  <Segmented
                    block
                    value={focus}
                    onChange={(value) => setFocus(String(value))}
                    options={[
                      { label: "All", value: "all" },
                      { label: "People", value: "people" },
                      { label: "Trips", value: "trips" },
                      { label: "Revenue", value: "revenue" },
                    ]}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <Text style={{ color: TEXT_MUTED, fontSize: 14, display: "block", marginBottom: 6 }}>Window</Text>
                  <Segmented
                    block
                    value={monthWindow}
                    onChange={(value) => setMonthWindow(Number(value))}
                    options={[
                      { label: "1M", value: 1 },
                      { label: "3M", value: 3 },
                      { label: "6M", value: 6 },
                    ]}
                  />
                </Col>

                <Col xs={24}>
                  <Space size={12} wrap>
                    <Badge color="#10b981" text={<span style={{ color: TEXT_MUTED, fontSize: 16 }}>Filtered revenue: {formatCurrency(filteredGrossRevenueTotal)} · Platform earnings: {formatCurrency(filteredRevenueTotal)}</span>} />
                    <Badge color="#22d3ee" text={<span style={{ color: TEXT_MUTED, fontSize: 16 }}>Registrations in view: {filteredRegistrationTotal}</span>} />
                    <Badge color="#6366f1" text={<span style={{ color: TEXT_MUTED, fontSize: 16 }}>Trip statuses shown: {filteredTripStatusBreakdown.length}</span>} />
                    <Button type="link" icon={<BarChartOutlined />} onClick={resetFilters} style={{ paddingInline: 0 }}>
                      Reset filters
                    </Button>
                  </Space>
                </Col>
                <Col xs={24}>
                  <Text style={{ color: TEXT_MUTED, fontSize: 14, display: "block", marginBottom: 6 }}>Quick Search & Go</Text>
                  <AutoComplete
                    style={{ width: "100%" }}
                    options={quickSearchOptions}
                    value={quickSearch}
                    onChange={setQuickSearch}
                    onSelect={(_value, option: any) => navigate(option.route)}
                    filterOption={(inputValue, option) => (option?.value ?? "").toUpperCase().includes(inputValue.toUpperCase())}
                  >
                    <Input.Search
                      placeholder="Search people, trips, cities, or modules and press Enter"
                      enterButton={<><SearchOutlined /> Go</>}
                      onSearch={onQuickSearch}
                    />
                  </AutoComplete>
                </Col>
          </Row>
        </div>
      </Card>

      <Row gutter={[20, 20]} style={{ marginBottom: 24, marginTop: 24 }}>
        <Col xs={24}>
          <AiAgentWidget />
        </Col>
      </Row>

      <Spin spinning={loading} size="large">
        <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
          {visibleKpis.map((kpi, idx) => (
            <Col xs={24} sm={12} xl={6} key={idx}>
              <Card
                onClick={() => navigate(kpi.route)}
                style={{
                  background: CARD_BG,
                  border: `1px solid ${CARD_BORDER}`,
                  borderRadius: 16,
                  overflow: "hidden",
                  position: "relative",
                  boxShadow: "0 10px 28px rgba(15, 23, 42, 0.07)",
                  cursor: "pointer",
                }}
                bodyStyle={{ padding: "20px 24px" }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    background: kpi.gradient,
                    borderRadius: "4px 0 0 4px",
                  }}
                />
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <div>
                    <Text style={{ color: TEXT_MUTED, fontSize: 14, textTransform: "uppercase", letterSpacing: 1 }}>
                      {kpi.title}
                    </Text>
                    <div style={{ marginTop: 6 }}>
                      {kpi.isCurrency ? (
                        <Title level={3} style={{ color: TEXT_PRIMARY, margin: 0, fontWeight: 700 }}>
                          {kpi.value}
                        </Title>
                      ) : (
                        <Statistic
                          value={kpi.value as number}
                          valueStyle={{ color: TEXT_PRIMARY, fontSize: 28, fontWeight: 700 }}
                        />
                      )}
                    </div>
                    <Text style={{ color: TEXT_MUTED, fontSize: 14, marginTop: 4, display: "block" }}>
                      {kpi.subValue}
                    </Text>
                  </div>
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      background: `${kpi.color}20`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 22,
                      color: kpi.color,
                    }}
                  >
                    {kpi.icon}
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
          {showRevenueSection && (
          <Col xs={24} lg={showTripsSection ? 14 : 24}>
            <Card
              title={
                <span style={{ color: TEXT_PRIMARY, fontWeight: 600 }}>
                  <RiseOutlined style={{ color: "#f59e0b", marginRight: 8 }} />
                  Revenue ({monthWindow} Month{monthWindow > 1 ? "s" : ""})
                </span>
              }
              style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}`, borderRadius: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
              headStyle={{ background: CARD_BG, borderBottom: `1px solid ${CARD_BORDER}`, color: TEXT_PRIMARY }}
              bodyStyle={{ background: CARD_BG, paddingTop: 12 }}
            >
              {filteredRevenueByMonth.length === 0 ? (
                <Empty description={<span style={{ color: TEXT_MUTED }}>No data</span>} />
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={filteredRevenueByMonth} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <defs>
                      <linearGradient id="grossRevenueGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e8eaf0" />
                    <XAxis dataKey="month" stroke={TEXT_MUTED} tick={{ fontSize: 14 }} />
                    <YAxis stroke={TEXT_MUTED} tick={{ fontSize: 14 }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip
                      contentStyle={{ background: "#fff", border: "1px solid #e8eaf0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }}
                      labelStyle={{ color: TEXT_PRIMARY }}
                      formatter={(val: any, name: any) => [formatCurrency(val), name]}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="grossRevenue" name="Overall Revenue" stroke="#0ea5e9" strokeWidth={2} fill="url(#grossRevenueGrad)" />
                    <Area type="monotone" dataKey="revenue" name="Platform Earnings" stroke="#f59e0b" strokeWidth={2} fill="url(#revenueGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Card>
          </Col>
          )}

          {showTripsSection && (
          <Col xs={24} lg={showRevenueSection ? 10 : 24}>
            <Card
              title={
                <span style={{ color: TEXT_PRIMARY, fontWeight: 600 }}>
                  <CalendarOutlined style={{ color: "#6366f1", marginRight: 8 }} />
                  Trip Status (Last 30 Days)
                </span>
              }
              style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}`, borderRadius: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
              headStyle={{ background: CARD_BG, borderBottom: `1px solid ${CARD_BORDER}`, color: TEXT_PRIMARY }}
              bodyStyle={{ background: CARD_BG, paddingTop: 12 }}
            >
              {filteredTripStatusBreakdown.length === 0 ? (
                <Empty description={<span style={{ color: TEXT_MUTED }}>No data</span>} />
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={filteredTripStatusBreakdown}
                      dataKey="tripCount"
                      nameKey="statusName"
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={3}
                    >
                      {filteredTripStatusBreakdown.map((_entry, index) => (
                        <Cell key={index} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: "#fff", border: "1px solid #e8eaf0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }}
                      labelStyle={{ color: TEXT_PRIMARY }}
                    />
                    <Legend
                      formatter={(value) => <span style={{ color: TEXT_MUTED, fontSize: 14 }}>{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card>
          </Col>
          )}
        </Row>

        <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
          {showPeopleSection && (
          <Col xs={24} lg={(showTripsSection || showRevenueSection) ? 14 : 24}>
            <Card
              title={
                <span style={{ color: TEXT_PRIMARY, fontWeight: 600 }}>
                  <TeamOutlined style={{ color: "#22d3ee", marginRight: 8 }} />
                  New Registrations ({monthWindow} Month{monthWindow > 1 ? "s" : ""})
                </span>
              }
              style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}`, borderRadius: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
              headStyle={{ background: CARD_BG, borderBottom: `1px solid ${CARD_BORDER}`, color: TEXT_PRIMARY }}
              bodyStyle={{ background: CARD_BG, paddingTop: 12 }}
            >
              {filteredRegistrationsByMonth.length === 0 ? (
                <Empty description={<span style={{ color: TEXT_MUTED }}>No data</span>} />
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={filteredRegistrationsByMonth} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e8eaf0" />
                    <XAxis dataKey="month" stroke={TEXT_MUTED} tick={{ fontSize: 14 }} />
                    <YAxis stroke={TEXT_MUTED} tick={{ fontSize: 14 }} />
                    <Tooltip
                      contentStyle={{ background: "#fff", border: "1px solid #e8eaf0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }}
                      labelStyle={{ color: TEXT_PRIMARY }}
                    />
                    <Legend formatter={(value) => <span style={{ color: TEXT_MUTED, fontSize: 14 }}>{value}</span>} />
                    <Bar dataKey="attendants" name="Attendants" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="travellers" name="Travellers" fill="#22d3ee" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
          </Col>
          )}

          {showTripsSection && (
          <Col xs={24} lg={(showPeopleSection || showRevenueSection) ? 10 : 24}>
            <Card
              title={
                <span style={{ color: TEXT_PRIMARY, fontWeight: 600 }}>
                  <EnvironmentOutlined style={{ color: "#10b981", marginRight: 8 }} />
                  Top Cities by Trips
                </span>
              }
              style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}`, borderRadius: 16, height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
              headStyle={{ background: CARD_BG, borderBottom: `1px solid ${CARD_BORDER}`, color: TEXT_PRIMARY }}
              bodyStyle={{ background: CARD_BG, paddingTop: 16 }}
            >
              {filteredTopCities.length === 0 ? (
                <Empty description={<span style={{ color: TEXT_MUTED }}>No data</span>} />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {filteredTopCities.map((city, idx) => {
                    const maxCount = filteredTopCities[0]?.tripCount ?? 1;
                    const pct = Math.round((city.tripCount / maxCount) * 100);
                    return (
                      <div key={idx}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <Text style={{ color: TEXT_PRIMARY, fontSize: 15 }}>
                            <span
                              style={{
                                display: "inline-block",
                                width: 20,
                                height: 20,
                                borderRadius: "50%",
                                background: COLORS[idx % COLORS.length],
                                color: "#fff",
                                fontSize: 12,
                                fontWeight: 700,
                                textAlign: "center",
                                lineHeight: "20px",
                                marginRight: 8,
                              }}
                            >
                              {idx + 1}
                            </span>
                            {city.cityName}
                          </Text>
                          <Text style={{ color: TEXT_MUTED, fontSize: 14 }}>{city.tripCount} trips</Text>
                        </div>
                        <Progress
                          percent={pct}
                          showInfo={false}
                          strokeColor={COLORS[idx % COLORS.length]}
                          trailColor="#e8eaf0"
                          size="small"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </Col>
          )}
        </Row>

        {showPeopleSection && (
        <Row gutter={[20, 20]}>
          <Col xs={24}>
            <Card
              title={
                <span style={{ color: TEXT_PRIMARY, fontWeight: 600 }}>
                  <UserOutlined style={{ color: "#a78bfa", marginRight: 8 }} />
                  Attendant Status Breakdown
                </span>
              }
              style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}`, borderRadius: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
              headStyle={{ background: CARD_BG, borderBottom: `1px solid ${CARD_BORDER}`, color: TEXT_PRIMARY }}
              bodyStyle={{ background: CARD_BG, paddingTop: 16 }}
            >
              {filteredAttendantStatus.length === 0 ? (
                <Empty description={<span style={{ color: TEXT_MUTED }}>No data</span>} />
              ) : (
                <Row gutter={[16, 16]}>
                  {filteredAttendantStatus.map((item, idx) => {
                    const total = filteredAttendantStatus.reduce((sum: number, s: any) => sum + s.count, 0);
                    const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
                    return (
                      <Col xs={24} sm={12} md={8} lg={6} key={idx}>
                        <div
                          style={{
                            background: "#f8f9fc",
                            border: `1px solid ${CARD_BORDER}`,
                            borderRadius: 12,
                            padding: "16px 20px",
                            display: "flex",
                            alignItems: "center",
                            gap: 16,
                          }}
                        >
                          <div style={{ position: "relative" }}>
                            <Progress
                              type="circle"
                              percent={pct}
                              size={56}
                              strokeColor={COLORS[idx % COLORS.length]}
                              trailColor="#e8eaf0"
                              format={() => (
                                <span style={{ color: TEXT_PRIMARY, fontSize: 12, fontWeight: 700 }}>{pct}%</span>
                              )}
                            />
                          </div>
                          <div>
                            <Text style={{ color: TEXT_MUTED, fontSize: 13, textTransform: "uppercase", letterSpacing: 0.8 }}>
                              {item.statusName}
                            </Text>
                            <div style={{ color: TEXT_PRIMARY, fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>
                              {item.count}
                            </div>
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              )}
            </Card>
          </Col>
        </Row>
        )}
      </Spin>
    </div>
  );
};

export default Dashboard;
